import { rememberFreeSessionLead } from "@/components/FreeSessionGateModal";
import { useLeadForm } from "@/hooks/useLeadForm";
import { formatUsPhoneInput } from "@/lib/leadValidation";
import { getAttribution } from "@/lib/attribution";
import { useRef, useState } from "react";
import LeadConsentNote from "@/components/LeadConsentNote";
import { normalizePhone, phoneInputProps } from "@/lib/phone";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Send, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { trackMetaEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import AdMeasurementNote from "@/components/AdMeasurementNote";
import { newEventId, sendCapiEvent } from "@/lib/capi";

interface FreeFirstClassCTAProps {
  /** Eyebrow above headline. Defaults to "Free First Session". */
  eyebrow?: string;
  /** Main headline. Defaults to a generic Woodstock pitch. */
  headline?: string;
  /** Sub-line under the headline. */
  subline?: string;
  /** Analytics source tag  -  pass the page slug so leads are attributable. */
  source?: string;
  /** Optional id for in-page anchor (e.g. "book"). */
  anchorId?: string;
  buttonLabel?: string;
  showBenefits?: boolean;
  showGoal?: boolean;
}

/**
 * Compact 3-field lead capture band (first name, email, phone).
 * Inserts on local landing pages + home, immediately near the hero,
 * to lift conversion before users have to scroll to the full LeadFormSection.
 */
const FreeFirstClassCTA = ({
  eyebrow = "Free First Session",
  headline = "CLAIM YOUR FREE FIRST SESSION",
  subline = "Coach-led semi-private training in Woodstock, GA. No card, no commitment  -  just one session to feel the difference.",
  source = "free_first_class_cta",
  anchorId,
  buttonLabel = "Claim a Free Session",
  showBenefits = true,
  showGoal = true,
}: FreeFirstClassCTAProps) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [goal, setGoal] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Honeypot  -  bots fill every field
  const [website, setWebsite] = useState("");
  const mountedAt = useRef<number>(Date.now());
  const navigate = useNavigate();

  const lf = useLeadForm({
    first_name: { kind: "first_name", value: firstName },
    email: { kind: "email", value: email, set: setEmail },
    phone: { kind: "phone", value: phone },
    goal: { kind: "select", value: goal, optional: !showGoal, message: "Please pick your #1 goal." },
  }, "FreeFirstClassCTA");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(await lf.validate())) return;

    if (website.trim() !== "" || Date.now() - mountedAt.current < 1500) {
      setSubmitted(true);
      navigate("/thank-you");
      return;
    }

    const cleanFirst = firstName.trim().slice(0, 100);
    const cleanEmail = email.trim().toLowerCase().slice(0, 255);
    const cleanPhone = normalizePhone(phone);

    if (!cleanFirst || !cleanEmail || !cleanPhone) return;

    setLoading(true);
    const { error } = await supabase.rpc("submit_free_session_lead", { p_entry: { attribution: getAttribution(),
      first_name: cleanFirst,
      last_name: null,
      email: cleanEmail,
      phone: cleanPhone,
      goals: goal ? [goal] : [],
      source,
    } });

    setLoading(false);

    if (error) {
      console.error("Free first class CTA error:", error);
      toast({
        title: "Hmm, something went wrong",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
      return;
    }

    setSubmitted(true);
    rememberFreeSessionLead({ first_name: cleanFirst, email: cleanEmail, phone: cleanPhone });
    const eventId = newEventId();
    sendCapiEvent("Lead", eventId, cleanEmail);
    trackMetaEvent("Lead", {
      content_name: "Free First Session  -  Inline CTA",
      content_category: "free_session_lead",
      currency: "USD",
      value: 99,
      source,
      goal,
      page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
    }, false, eventId);
    navigate("/thank-you");
  };

  return (
    <section
      id={anchorId}
      className="py-12 sm:py-16 section-padding relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-background border-y border-primary/30"
    >
      <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Pitch */}
        <div>
          <span className="inline-flex items-center gap-2 text-primary font-bold text-xs sm:text-sm uppercase tracking-widest">
            <Sparkles className="w-4 h-4" /> {eyebrow}
          </span>
          <h2 className="font-display text-3xl sm:text-5xl mt-3 mb-4 leading-[0.95]">
            <span className="text-gradient-honey">{headline}</span>
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-4 max-w-xl">
            {subline}
          </p>
          {showBenefits && <ul className="grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground max-w-xl">
            {[
              "No card required",
              "Month-to-month available",
              "Real coach in the room",
              "Built for adults 30-55+",
            ].map((b) => (
              <li key={b} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                {b}
              </li>
            ))}
          </ul>}
        </div>

        {/* Form */}
        <div className="lg:justify-self-end w-full max-w-md">
          {submitted ? (
            <div className="bg-card border border-primary/40 rounded-2xl p-8 text-center glow-honey-sm">
              <div
                className="w-16 h-16 bg-gradient-honey flex items-center justify-center mx-auto mb-4"
                style={{ clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }}
              >
                <CheckCircle2 className="w-8 h-8 text-primary-foreground" />
              </div>
              <h3 className="font-display text-2xl mb-2">WELCOME TO THE HIVE!</h3>
              <p className="text-muted-foreground text-sm">
                We'll text you shortly to lock in your free first session.
              </p>
            </div>
          ) : (
            <form noValidate
              onSubmit={handleSubmit}
              className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-3 shadow-xl"
              aria-label="Claim your free first session"
            >
              {/* Honeypot */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}
              >
                <label htmlFor={`hp-${source}`}>Website</label>
                <input
                  id={`hp-${source}`}
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <h3 className="font-display text-xl text-center">
                START WITH ONE FREE SESSION
              </h3>

              <input {...lf.field("first_name")}
                type="text"
                required
                maxLength={100}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                aria-label="First name"
                className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none text-sm"
              />
{lf.message("first_name")}
              <input {...lf.field("email")}
                type="email"
                required
                maxLength={255}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                aria-label="Email"
                className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none text-sm"
              />
{lf.message("email")}
              <input {...lf.field("phone")}
                type="tel"
{...phoneInputProps}
                value={phone}
                onChange={(e) => setPhone(formatUsPhoneInput(e.target.value))}
                placeholder="Mobile phone"
                aria-label="Mobile phone"
                className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none text-sm"
              />
{lf.message("phone")}

              {showGoal && <div>
                <label htmlFor={`goal-${source}`} className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
                  What's your #1 goal right now?
                </label>
                <select {...lf.field("goal")}
                  id={`goal-${source}`}
                  required
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground focus:border-primary focus:outline-none text-sm"
                >
                  <option value="" disabled>Pick one…</option>
                  <option value="Lose fat">Lose fat</option>
                  <option value="Build strength">Build strength</option>
                  <option value="More energy">More energy</option>
                  <option value="Move without pain">Move without pain</option>
                  <option value="All of the above">All of the above</option>
                </select>
{lf.message("goal")}
              </div>}

              <Button
                type="submit"
                disabled={loading}
                className="h-auto w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-gradient-honey font-bold uppercase tracking-wider text-primary-foreground hover:opacity-90 transition-opacity text-sm disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    {buttonLabel} <Send className="w-4 h-4" />
                  </>
                )}
              </Button>
<LeadConsentNote />
              <AdMeasurementNote />
              <p className="text-[11px] text-muted-foreground text-center">
                We'll text you to schedule · No card · No commitment · Availability is first come, first served.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default FreeFirstClassCTA;
